function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function adminHeaders(extra?: Record<string, string>): HeadersInit {
  const id = tenantId();
  return {
    "Content-Type": "application/json",
    /* Single name only — duplicate keys (X-tenant-id + X-Tenant-ID) merge into one comma value in fetch */
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...extra,
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg =
    pick(d.message) ??
    pick(d.detail) ??
    pick(d.error) ??
    (Array.isArray(d.non_field_errors) ? pick((d.non_field_errors as string[])[0]) : null);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

/** Derives a display name for OTP request when the UI only collects email. */
export function fullNameFromEmail(email: string): string {
  const local = email.trim().split("@")[0] ?? "";
  if (!local) {return "Administrator";}
  const words = local
    .replace(/[._+]+/g, " ")
    .trim()
    .split(/\s+/);
  return words
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ""))
    .filter(Boolean)
    .join(" ");
}

export function extractCodeReference(data: Record<string, unknown>): string | null {
  const anyData = data as {
    code_reference?: string;
    data?: { code_reference?: string };
    reference?: string;
  };
  if (typeof anyData.code_reference === "string") {return anyData.code_reference;}
  if (anyData.data && typeof anyData.data.code_reference === "string")
    {return anyData.data.code_reference;}
  if (typeof anyData.reference === "string") {return anyData.reference;}
  return null;
}

export async function requestAdminOtp(body: { email: string; full_name: string }) {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const url = `${base}/jain_gaurav_yatra/admin-login/request-otp/`;
  const res = await fetch(url, {
    method: "POST",
    headers: adminHeaders(),
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return data;
}

export async function verifyAdminOtp(body: { email: string; otp: string; code_reference: string }) {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  const url = `${base}/jain_gaurav_yatra/admin-login/verify-otp/`;
  const res = await fetch(url, {
    method: "POST",
    headers: adminHeaders({ "X-User-Timezone": tz }),
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return data;
}

export const ADMIN_SESSION_KEY = "jgy_admin_session";

export interface StoredAdminSession {
  email: string;
  session: Record<string, unknown>;
}

export function persistAdminSession(payload: StoredAdminSession) {
  try {
    localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

export function getStoredAdminSession(): StoredAdminSession | null {
  try {
    const raw = localStorage.getItem(ADMIN_SESSION_KEY);
    if (!raw) {return null;}
    return JSON.parse(raw) as StoredAdminSession;
  } catch {
    return null;
  }
}

/** True when an admin OTP session is stored (i.e. signed in within the app). */
export function isAdminLoggedIn(): boolean {
  return !!getStoredAdminSession();
}

/** Where the "Admin Portal" entry link should go: dashboard if signed in, else login. */
export function adminEntryPath(): "/admin" | "/admin/login" {
  return isAdminLoggedIn() ? "/admin" : "/admin/login";
}

export function clearAdminSession() {
  try {
    localStorage.removeItem(ADMIN_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Pulls access / refresh tokens out of the verify-otp session payload.
 * Tolerant of nesting since the backend shape isn't guaranteed
 * (top-level, `data.*`, `tokens.*`, and access/access_token variants).
 */
export function extractAdminTokens(session: Record<string, unknown> | null | undefined): {
  access: string | null;
  refresh: string | null;
} {
  const empty = { access: null as string | null, refresh: null as string | null };
  if (!session || typeof session !== "object") {return empty;}

  const str = (v: unknown) => (typeof v === "string" && v ? v : null);
  const candidates: Record<string, unknown>[] = [session];
  for (const key of ["data", "tokens", "session"]) {
    const nested = (session as Record<string, unknown>)[key];
    if (nested && typeof nested === "object") {candidates.push(nested as Record<string, unknown>);}
  }

  let access: string | null = null;
  let refresh: string | null = null;
  for (const c of candidates) {
    access = access ?? str(c.access) ?? str(c.access_token) ?? str(c.accessToken);
    refresh = refresh ?? str(c.refresh) ?? str(c.refresh_token) ?? str(c.refreshToken);
  }
  return { access, refresh };
}

/**
 * Exchanges the refresh token for a fresh access token.
 * The backend reads the session cookie too, so `refresh` may be empty.
 * Persists the rotated tokens back into the stored session on success.
 */
export async function refreshAdminToken(refresh?: string): Promise<Record<string, unknown>> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}

  const stored = getStoredAdminSession();
  const refreshToken = refresh ?? extractAdminTokens(stored?.session).refresh ?? "";

  const url = `${base}/jain_gaurav_yatra/token/refresh/`;
  const res = await fetch(url, {
    method: "POST",
    headers: adminHeaders(),
    credentials: "include",
    body: JSON.stringify({ refresh: refreshToken }),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {throw new Error(errMessage(data, res.status));}

  // Merge rotated tokens back into the persisted session.
  if (stored) {
    persistAdminSession({ email: stored.email, session: { ...stored.session, ...data } });
  }
  return data;
}

/**
 * Logs the admin out server-side and clears the local session.
 * Always clears local state, even if the network call fails.
 */
export async function logoutAdmin(): Promise<void> {
  const base = apiBase();
  const stored = getStoredAdminSession();
  const { access, refresh } = extractAdminTokens(stored?.session);

  try {
    if (base) {
      const url = `${base}/auth/logout/`;
      await fetch(url, {
        method: "POST",
        headers: adminHeaders(access ? { Authorization: `Bearer ${access}` } : undefined),
        credentials: "include",
        body: JSON.stringify({ refresh: refresh ?? "" }),
      });
    }
  } catch {
    /* ignore network failure — still clear local session below */
  } finally {
    clearAdminSession();
  }
}
